import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { gql } from 'graphql-request'
import { gqlClient } from '@/lib/graphql'
import { useAuth } from '@/hooks/useAuth'
import Logo from '@/components/Logo'

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.71-1.57 2.68-3.89 2.68-6.62Z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18Z"
      />
      <path
        fill="#FBBC05"
        d="M3.97 10.72A5.4 5.4 0 0 1 3.68 9c0-.6.1-1.18.29-1.72V4.95H.96A9 9 0 0 0 0 9c0 1.45.35 2.83.96 4.05l3.01-2.33Z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58A9 9 0 0 0 9 0 9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58Z"
      />
    </svg>
  )
}

const SIGN_IN = gql`
  mutation SignIn($email: String!, $password: String!) {
    signIn(input: { email: $email, password: $password }) {
      token
      errors
    }
  }
`

const SIGN_UP = gql`
  mutation SignUp($email: String!, $password: String!, $name: String) {
    signUp(input: { email: $email, password: $password, name: $name }) {
      token
      errors
    }
  }
`

type AuthPayload = { token: string | null; errors: string[] }

export default function Login() {
  const { login } = useAuth()
  const navigate = useNavigate()
  const [mode, setMode] = useState<'signin' | 'signup'>('signin')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [name, setName] = useState('')
  const [errors, setErrors] = useState<string[]>([])
  const [busy, setBusy] = useState(false)

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setErrors([])
    try {
      const res =
        mode === 'signin'
          ? await gqlClient.request<{ signIn: AuthPayload }>(SIGN_IN, { email, password })
          : await gqlClient.request<{ signUp: AuthPayload }>(SIGN_UP, { email, password, name })
      const payload = mode === 'signin' ? (res as { signIn: AuthPayload }).signIn : (res as { signUp: AuthPayload }).signUp
      if (payload.token) {
        await login(payload.token)
        navigate('/')
      } else {
        setErrors(payload.errors)
      }
    } catch {
      setErrors(['Something went wrong. Please try again.'])
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center px-6">
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center mb-8">
          <Logo size={64} className="rounded-2xl shadow-lg shadow-indigo-500/20" />
          <h1 className="mt-4 text-3xl font-semibold text-center text-slate-900">TransliFlow</h1>
          <p className="text-center text-slate-500">Translate &amp; read books</p>
        </div>

        <a
          href="/users/auth/google_oauth2"
          className="flex items-center justify-center gap-3 w-full rounded-lg border border-slate-300 bg-white py-2.5 font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 hover:shadow"
        >
          <GoogleIcon />
          Continue with Google
        </a>

        <div className="my-6 flex items-center gap-3 text-xs text-slate-400">
          <span className="h-px flex-1 bg-slate-200" /> or <span className="h-px flex-1 bg-slate-200" />
        </div>

        <form onSubmit={submit} className="space-y-3">
          {mode === 'signup' && (
            <input
              className="w-full rounded-lg border border-slate-300 px-3 py-2.5"
              placeholder="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          )}
          <input
            className="w-full rounded-lg border border-slate-300 px-3 py-2.5"
            type="email"
            placeholder="Email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <input
            className="w-full rounded-lg border border-slate-300 px-3 py-2.5"
            type="password"
            placeholder="Password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          {errors.length > 0 && (
            <ul className="text-sm text-red-600 list-disc pl-5">
              {errors.map((err) => (
                <li key={err}>{err}</li>
              ))}
            </ul>
          )}
          <button
            type="submit"
            disabled={busy}
            className="w-full rounded-lg bg-slate-900 py-2.5 font-medium text-white hover:bg-slate-800 disabled:opacity-50"
          >
            {busy ? '…' : mode === 'signin' ? 'Sign in' : 'Create account'}
          </button>
        </form>

        <button
          className="mt-4 w-full text-sm text-slate-500 hover:text-slate-700"
          onClick={() => {
            setMode(mode === 'signin' ? 'signup' : 'signin')
            setErrors([])
          }}
        >
          {mode === 'signin' ? "No account? Sign up" : 'Have an account? Sign in'}
        </button>
      </div>
    </div>
  )
}
